import type { UsageByToolRow } from "../apis/usageApi";
import { formatAggregateTimeSavedForSummary } from "../mspAnalytics/automatableDashboardShared";

export type UsageWorkCategoryId =
  | "identity"
  | "psa"
  | "rmm"
  | "documentation"
  | "collaboration"
  | "utility"
  | "other";

export interface UsageWorkCategory {
  id: UsageWorkCategoryId;
  label: string;
  description: string;
  minutesPerAction: number;
  /** Example tool names used in the est. time saved help tooltip. */
  examples: string[];
}

export const USAGE_WORK_CATEGORIES: UsageWorkCategory[] = [
  {
    id: "identity",
    label: "Identity & access",
    description: "Microsoft 365 and Google Workspace user, group, and license actions",
    minutesPerAction: 5,
    examples: ["m365_reset_password", "google_add_user_to_group"],
  },
  {
    id: "psa",
    label: "PSA & ticketing",
    description: "Ticket updates, notes, and PSA workflow actions",
    minutesPerAction: 4,
    examples: ["autotask_add_ticket_note", "halo_update_ticket_status"],
  },
  {
    id: "rmm",
    label: "RMM & devices",
    description: "Remote monitoring, scripts, and device management",
    minutesPerAction: 6,
    examples: ["datto_run_script", "ninjaone_execute_script"],
  },
  {
    id: "documentation",
    label: "Documentation & KB",
    description: "Knowledge base search, IT Glue, Hudu, and reference lookups",
    minutesPerAction: 3,
    examples: ["search_kb", "itglue_get_document"],
  },
  {
    id: "collaboration",
    label: "Collaboration",
    description: "Slack messages and customer notifications",
    minutesPerAction: 2,
    examples: ["slack_send_message", "m365_send_email"],
  },
  {
    id: "utility",
    label: "Investigation & utility",
    description: "Ticket suggestions, searches, and assistant workflows",
    minutesPerAction: 3,
    examples: ["ticket_suggestions", "list_customers"],
  },
  {
    id: "other",
    label: "Other automation",
    description: "Additional Theo tool runs",
    minutesPerAction: 4,
    examples: ["custom tool names not matched above"],
  },
];

const CATEGORY_BY_ID = new Map(USAGE_WORK_CATEGORIES.map((c) => [c.id, c]));

export function categorizeUsageToolName(toolName: string): UsageWorkCategoryId {
  const t = String(toolName || "").trim().toLowerCase();
  if (!t || t === "(unknown)") return "other";

  if (
    t.startsWith("m365_") ||
    t.startsWith("ms_graph_") ||
    t.startsWith("google_") ||
    t.includes("password") ||
    t.includes("license") ||
    t.includes("mailbox")
  ) {
    return "identity";
  }

  if (
    t.includes("suggestion") ||
    t.includes("ticket_suggestions")
  ) {
    return "utility";
  }

  if (
    t.startsWith("autotask_") ||
    t.startsWith("connectwise_") ||
    t.startsWith("halo_") ||
    t.startsWith("superops_") ||
    t.startsWith("ninjaone_psa_") ||
    t.includes("ticket") ||
    t.includes("psa_")
  ) {
    return "psa";
  }

  if (
    t.startsWith("datto_") ||
    t.startsWith("nable_") ||
    t.includes("script_execution") ||
    t.includes("rmm_") ||
    (t.startsWith("ninjaone_") && !t.startsWith("ninjaone_psa_"))
  ) {
    return "rmm";
  }

  if (
    t.startsWith("itglue_") ||
    t.startsWith("hudu_") ||
    t.includes("search_kb") ||
    t.includes("kb_") ||
    t.includes("knowledge")
  ) {
    return "documentation";
  }

  if (t.startsWith("slack_") || t.includes("send_email") || t.includes("compose_email") || t.includes("notify")) {
    return "collaboration";
  }

  if (t.includes("search_") || t.startsWith("list_") || t.startsWith("get_")) {
    return "utility";
  }

  return "other";
}

export function humanizeUsageToolName(toolName: string): string {
  return String(toolName || "")
    .split("_")
    .map((word) => (word ? word.charAt(0).toUpperCase() + word.slice(1) : ""))
    .join(" ");
}

export function estimatedMinutesForToolAction(toolName: string): number {
  const categoryId = categorizeUsageToolName(toolName);
  return CATEGORY_BY_ID.get(categoryId)?.minutesPerAction ?? 4;
}

export interface UsageCategoryAggregate {
  id: UsageWorkCategoryId;
  label: string;
  description: string;
  actions: number;
  ticketActions: number;
  assistantActions: number;
  estimatedMinutesSaved: number;
}

export function aggregateUsageByCategory(rows: UsageByToolRow[]): UsageCategoryAggregate[] {
  const buckets = new Map<UsageWorkCategoryId, UsageCategoryAggregate>();

  for (const category of USAGE_WORK_CATEGORIES) {
    buckets.set(category.id, {
      id: category.id,
      label: category.label,
      description: category.description,
      actions: 0,
      ticketActions: 0,
      assistantActions: 0,
      estimatedMinutesSaved: 0,
    });
  }

  for (const row of rows) {
    const toolName = String(row.tool_name || "");
    const actions = Number(row.actions) || 0;
    if (actions <= 0) continue;

    const categoryId = categorizeUsageToolName(toolName);
    const bucket = buckets.get(categoryId);
    if (!bucket) continue;

    const minutesPerAction = estimatedMinutesForToolAction(toolName);
    bucket.actions += actions;
    bucket.ticketActions += Number(row.ticket_actions) || 0;
    bucket.assistantActions += Number(row.assistant_actions) || 0;
    bucket.estimatedMinutesSaved += actions * minutesPerAction;
  }

  return USAGE_WORK_CATEGORIES.map((c) => buckets.get(c.id)!)
    .filter((row) => row.actions > 0)
    .sort((a, b) => b.actions - a.actions);
}

export function sumEstimatedMinutesFromToolRows(rows: UsageByToolRow[]): number {
  return rows.reduce((sum, row) => {
    const actions = Number(row.actions) || 0;
    if (actions <= 0) return sum;
    return sum + actions * estimatedMinutesForToolAction(String(row.tool_name || ""));
  }, 0);
}

export function formatUsageInsightsHours(minutes: number): string {
  return formatAggregateTimeSavedForSummary(minutes);
}

export function buildUsageInsightsSummaryText(args: {
  totalActions: number;
  ticketActions: number;
  assistantActions: number;
  ticketsTouched: number;
  activeUsers: number;
  estimatedMinutesSaved: number;
  topCategoryLabel?: string;
}): string {
  const hoursLabel = formatUsageInsightsHours(args.estimatedMinutesSaved);
  const parts: string[] = [];

  parts.push(
    `Theo completed ${args.totalActions.toLocaleString()} tool action${args.totalActions === 1 ? "" : "s"} in this period`
  );

  if (args.ticketActions > 0 || args.assistantActions > 0) {
    const detail: string[] = [];
    if (args.ticketActions > 0) {
      detail.push(`${args.ticketActions.toLocaleString()} on PSA tickets`);
    }
    if (args.assistantActions > 0) {
      detail.push(`${args.assistantActions.toLocaleString()} in assistant workflows`);
    }
    parts.push(`including ${detail.join(" and ")}`);
  }

  if (args.ticketsTouched > 0) {
    parts.push(`across ${args.ticketsTouched.toLocaleString()} ticket${args.ticketsTouched === 1 ? "" : "s"}`);
  }

  if (args.activeUsers > 0) {
    parts.push(`with ${args.activeUsers} technician${args.activeUsers === 1 ? "" : "s"} actively using Theo`);
  }

  parts.push(`Estimated time saved: ${hoursLabel}`);

  if (args.topCategoryLabel) {
    parts.push(`Most activity: ${args.topCategoryLabel}`);
  }

  return `${parts.join(", ")}.`;
}

/**
 * Tool audits record the turn's actor, which is not always a person: unattended agent runs write
 * `autoagent`, older system turns `system`, and some rows carry no actor at all.
 */
const NON_PERSON_ACTORS: Record<string, string> = {
  autoagent: "Theo agents (autonomous)",
  system: "Theo system",
  neo: "Theo",
  "(unknown)": "Unknown user",
};

/** A Usage "by user" row's label: the person's email, or a plain name for a non-person actor. */
export function usageActorLabel(email: string | null | undefined): string {
  const value = String(email ?? "").trim();
  if (!value) return NON_PERSON_ACTORS["(unknown)"];
  return NON_PERSON_ACTORS[value.toLowerCase()] ?? value;
}
